import { useAccounts } from '@api/hooks';
import type { SwiftPayment } from '@api/hooks/types/createTransaction';
import type { Account } from '@api/hooks/useAccounts';
import { useCreateTransaction } from '@api/hooks/useTransactions';
import { ControlledAccountList } from '@components/AccountList';
import Alert from '@components/alert';
import { ControlledTextInput } from '@components/ControlledTextInput';
import { DocumentNumberInput } from '@components/DocumentNumberInput';
import { FormBlocker } from '@components/FormBlocker';
import { InputDropdown } from '@components/input';
import { PermissionGate } from '@components/PermissionGate';
import TemplateFromPayment from '@components/templates/TemplateFromPayment';
import { PaymentTfa, SigningStatus } from '@components/tfa/PaymentTfa';
import { DATE_FORMAT } from '@constants';
import { useBankProfile } from '@contexts/BankProfileContext';
import { useFeatureContext } from '@contexts/FeatureContext';
import { useGeoDataContext } from '@contexts/GeoDataContext';
import {
	Button,
	Collapsible,
	DateStringPicker,
	SelectInput,
	Typography,
} from '@gurupay/gp-ui-library';
import { useCreateTemplate } from '@hooks/useCreateTemplate';
import { useFF } from '@hooks/useFF.ts';
import { useBicValidation } from '@pages/payments/hooks/useBicValidation.ts';
import { useIbanBicMismatchSync } from '@pages/payments/hooks/useIbanBicMismatchSync.ts';
import { useSwiftIbanValidations } from '@pages/payments/hooks/useSwiftIbanValidations.ts';
import {
	resolveAddressMode,
	resolveTemplateAddressModes,
} from '@pages/payments/utils/addressUtils.ts';
import { PaymentAddressInput, PaymentDetailsSwitcher } from '@pages/templates/components';
import { useQueryClient } from '@tanstack/react-query';
import { prepareTemplate } from '@utils/utils';
import { addYears, format, startOfDay } from 'date-fns';
import { enUS, lt } from 'date-fns/locale';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { CC_FEATURE_FLAGS } from 'types/layout.ts';
import { AccountStatus } from 'types/operations.ts';
import { Capability } from 'types/roles';
import type { PaymentTemplate } from 'types/templates';

const ACCOUNTS_PAGE_SIZE = 10;
const today = startOfDay(new Date());
enum FormStatus {
	INITIAL = 'initial',
	CREATED = 'created',
}

const DEFAULT_VALUES = {
	currency: 'EUR',
	payment_type: 'INTERNATIONAL',
	payment_urgency: 'NORMAL',
	fee_type: 'SHA',
	execution_date: format(today, DATE_FORMAT),
	beneficiary: {
		address: {
			type: 'STRUCTURED',
		},
		bank: {
			address: {
				type: 'STRUCTURED',
			},
		},
	},
	correspondent: {
		address: {
			type: 'STRUCTURED',
		},
	},
	save_as_template: false,
} as Partial<SwiftPayment>;

const FEE_OPTIONS = [
	{ value: 'SHA', label: 'SHA' },
	// { value: 'OUR', label: 'OUR' },
];

interface SwiftPaymentProps {
	operation?: SwiftPayment;
	selectedAccount?: Account;
	template?: PaymentTemplate;
}

export const SwiftPaymentForm: FC<SwiftPaymentProps> = ({
	operation,
	template,
	selectedAccount,
}) => {
	const { selectedProfile } = useBankProfile();
	const featureContext = useFeatureContext();
	const hasAddressAligned = useFF(CC_FEATURE_FLAGS.ADDRESS_ALIGNMENT);
	const paymentForm = useForm<SwiftPayment>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: DEFAULT_VALUES,
		values: {
			...DEFAULT_VALUES,
			...resolveTemplateAddressModes(template, hasAddressAligned),
			...(operation && {
				// Currently account id in payments is string in an account it is number
				account_uuid: operation.account_uuid,
				amount: operation.amount,
				currency: operation.currency,
				payment_details: operation.payment_details,
				correspondent: operation?.correspondent
					? {
							...operation.correspondent,
							address: {
								...operation?.correspondent?.address,
								type: resolveAddressMode(
									operation?.correspondent?.address,
									hasAddressAligned,
								),
							},
						}
					: {
							address: {
								type: 'STRUCTURED',
							},
						},
				beneficiary: {
					name: operation.beneficiary.name,
					account: operation.beneficiary.account_number,
					bank: {
						...operation?.beneficiary?.bank,
						address: {
							...operation?.beneficiary?.bank?.address,
							type: resolveAddressMode(
								operation?.beneficiary?.bank?.address,
								hasAddressAligned,
							),
						},
					},
					...(operation.beneficiary.address && {
						address: {
							...operation.beneficiary.address,
							type: resolveAddressMode(operation.beneficiary.address, hasAddressAligned),
						},
					}),
				},
			}),
		},
	});

	if (
		selectedProfile &&
		featureContext?.data?.status !== 'OPENED' &&
		!featureContext?.isLoading
	) {
		return <Navigate replace to={'/payments'} />;
	}

	return (
		<FormProvider {...paymentForm}>
			<SwiftPaymentFormContent selectedAccount={selectedAccount} />
		</FormProvider>
	);
};

const SwiftPaymentFormContent: FC<Pick<SwiftPaymentProps, 'selectedAccount'>> = ({
	selectedAccount,
}) => {
	const { setError, register, handleSubmit, formState, control, watch, setValue, reset } =
		useFormContext<SwiftPayment>();

	const fromAccountId = watch('account_uuid');
	const queryClient = useQueryClient();
	const {
		validate: validateIban,
		isValidating: isIbanValidating,
		ibanBicMask,
	} = useSwiftIbanValidations();

	const ibanBicMaskRef = useIbanBicMismatchSync(ibanBicMask, 'beneficiary.bank.bic');

	const { t, i18n } = useTranslation();
	const benBic = useBicValidation(
		'beneficiary.bank',
		ibanBicMaskRef,
		t('payments.common.bicPatternValidation'),
	);
	const corrBic = useBicValidation(
		'correspondent',
		undefined,
		t('payments.newPayment.additionalData.swift.additionalData.bicPatternValidation'),
	);
	const {
		data: createdTransaction,
		mutateAsync: createTransaction,
		isError: failedToCreateTransaction,
		isPending,
		error: submitError,
	} = useCreateTransaction();

	const { isError } = useAccounts({
		limit: ACCOUNTS_PAGE_SIZE,
		status: [AccountStatus.OPENED, AccountStatus.CREDIT_BLOCKED],
	});

	const geoData = useGeoDataContext();

	const [signingStatus, setSigningStatus] = useState<SigningStatus | undefined>();

	const statusMessageRef = useRef<HTMLDivElement>(null);
	const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.INITIAL);

	const { mutate: createTemplate } = useCreateTemplate({ errorKey: 'failedAs' });

	const errors = useMemo(() => formState.errors, [formState]);

	useEffect(() => {
		if (submitError?.response?.status !== 422) {
			return;
		}

		const errorsValidation = Object.entries(submitError?.response?.data?.errors ?? {});

		if (errorsValidation?.length > 0) {
			errorsValidation.forEach(([key, error]) => {
				setError(key as keyof SwiftPayment, {
					message: error,
				});
			});
		}
	}, [submitError?.response]);

	const onSubmit: SubmitHandler<SwiftPayment> = (data) => {
		const {
			save_as_template,
			template_name,
			document_number,
			execution_date,
			...restPaymentDetails
		} = data;

		const formData: SwiftPayment = {
			...restPaymentDetails,
			document_number: document_number.toString(),
			execution_date,
		};

		createTransaction(
			{ data: formData },
			{
				onSuccess: () => {
					if (save_as_template && template_name) {
						createTemplate(
							prepareTemplate({
								template_name,
								...restPaymentDetails,
							}),
						);
					}
					setFormStatus(FormStatus.CREATED);
				},
			},
		);
	};

	const resetForm = (status: SigningStatus) => {
		reset(undefined, { keepDefaultValues: true, keepDirty: false });
		ibanBicMaskRef.current = null;
		benBic.clearBicData();
		corrBic.clearBicData();
		setSigningStatus(status);
		setFormStatus(FormStatus.INITIAL);
		statusMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
		queryClient.refetchQueries({ queryKey: ['document-number', fromAccountId] });
	};

	const setDefaultValue = (accountId: string) => {
		setValue('account_uuid', accountId);
	};

	return (
		<>
			{isError && <Alert type="error">{t('common.failedFetching')}</Alert>}
			<form className="space-y-space-40 mt-space-40" onSubmit={handleSubmit(onSubmit)}>
				<Alert type="info">{t('payments.international.alert')}</Alert>
				<ControlledAccountList
					defaultValue={selectedAccount}
					label={t('payments.common.from')}
					name="account_uuid"
					setDefaultValue={selectedAccount?.uuid ? undefined : setDefaultValue}
				/>
				{Object.keys(formState.touchedFields).length === 0 && (
					<div ref={statusMessageRef}>
						{signingStatus === SigningStatus.ERROR && (
							<Alert type={'error'}>{t('payments.common.orderSigningFailed')}</Alert>
						)}
						{signingStatus === SigningStatus.SUCCESS && (
							<Alert type={'success'}>{t('payments.common.orderSigned')}</Alert>
						)}
						{signingStatus === SigningStatus.CANCELED && (
							<Alert type={'warning'}>{t('payments.common.orderSigningCanceled')}</Alert>
						)}
					</div>
				)}
				<div className="space-y-space-16">
					<Typography level="h4">
						{t('payments.newPayment.additionalData.beneficiary')}
					</Typography>
					<ControlledTextInput
						disabled={formStatus !== FormStatus.INITIAL}
						label={t('payments.newPayment.form.name')}
						name="beneficiary.name"
						rules={{
							required: t('payments.newPayment.fieldIsRequired'),
							maxLength: {
								message: t('payments.common.maxLength', { maxLength: 70 }),
								value: 70,
							},
						}}
					/>
					<ControlledTextInput
						disabled={formStatus !== FormStatus.INITIAL}
						isLoading={isIbanValidating}
						label={t('payments.newPayment.form.accountNo')}
						name="beneficiary.account"
						rules={{
							required: t('payments.newPayment.fieldIsRequired'),
							validate: validateIban,
						}}
					/>
				</div>
				<InputDropdown
					control={control}
					disableTyping={formStatus !== FormStatus.INITIAL}
					inputMode="decimal"
					inputRegister={register('amount', { required: true })}
					isError={!!errors?.amount || !!errors?.currency}
					isMandatory
					label={t('payments.newPayment.form.amount')}
					options={geoData?.data?.currencies ?? []}
					selectorRegister={register('currency', { required: true })}
				/>
				{(errors?.amount || errors?.currency) && (
					<Typography className="text-status-error-800" level="small-text">
						{errors?.amount?.message || errors?.currency?.message}
					</Typography>
				)}

				<PaymentAddressInput<SwiftPayment>
					prefix="beneficiary.address"
					translatePrefix={'payments.newPayment.additionalData.address'}
					disabled={formStatus !== FormStatus.INITIAL}
					required
				/>
				<div className="space-y-space-16">
					<Typography level="h2">{t('payments.newPayment.data.title')}</Typography>
					<Typography level="h4">
						{t('payments.newPayment.data.transferType.label')}
					</Typography>
					<Controller
						control={control}
						name="payment_details"
						render={({ field }) => (
							<PaymentDetailsSwitcher<SwiftPayment>
								{...field}
								error={errors.payment_details}
								isDisabled={formStatus !== FormStatus.INITIAL}
								required
							/>
						)}
						rules={{
							required: t('payments.newPayment.fieldIsRequired'),
							maxLength: {
								message: t('payments.common.maxLength', { maxLength: 140 }),
								value: 140,
							},
						}}
					/>
					<div>
						<Controller
							control={control}
							name={'execution_date'}
							render={({ field }) => (
								<DateStringPicker
									{...field}
									className="mt-space-16"
									disabledPast
									endMonth={addYears(today, 1)}
									error={errors.execution_date?.message}
									isDisabled={formStatus !== FormStatus.INITIAL}
									label={t('payments.newPayment.data.date')}
									locale={i18n.language === 'lt' ? lt : enUS}
									startDate={today}
									startMonth={new Date(2015, 1)}
								/>
							)}
							rules={{ required: t('payments.newPayment.fieldIsRequired') }}
						/>
						{errors.execution_date && (
							<Typography className="text-status-error-800" level="small-text">
								{errors?.execution_date.message}
							</Typography>
						)}
					</div>
					<Controller
						control={control}
						name="fee_type"
						render={({ field }) => (
							<SelectInput
								{...field}
								className="max-w-125"
								error={errors.fee_type?.message}
								isDisabled
								isRequired
								label={t('payments.createdPayment.additionalPaymentData.feeType')}
								options={FEE_OPTIONS} // FEE types?
							/>
						)}
					/>

					<DocumentNumberInput
						accountId={fromAccountId}
						disabled={formStatus !== FormStatus.INITIAL}
					/>
					<Typography className="mt-space-40" level="h4">
						{t('payments.newPayment.additionalData.beneficiaryBank')}
					</Typography>
					<div>
						<ControlledTextInput
							disabled={formStatus !== FormStatus.INITIAL}
							isLoading={benBic.isValidating}
							label={t('payments.newPayment.additionalData.swift.bic')}
							name="beneficiary.bank.bic"
							placeholder="GUPULT22XXX"
							rules={{
								required: t('payments.newPayment.fieldIsRequired'),
								validate: benBic.validate,
							}}
						/>
						{benBic.warning && (
							<Typography
								className="mt-space-4 text-status-warning-500"
								level="small-text"
							>
								{benBic.warning}
							</Typography>
						)}
					</div>

					<ControlledTextInput
						disabled={formStatus !== FormStatus.INITIAL || benBic.isLocked}
						label={t('payments.newPayment.additionalData.swift.additionalData.name')}
						name="beneficiary.bank.name"
						rules={{
							required: t('payments.newPayment.fieldIsRequired'),
						}}
					/>

					<PaymentAddressInput<SwiftPayment>
						prefix="beneficiary.bank.address"
						translatePrefix="payments.newPayment.additionalData.address"
						disabled={formStatus !== FormStatus.INITIAL || benBic.isLocked}
						required
						unstructuredLineLabelKey="bankLine"
					/>
				</div>

				<Collapsible title={t('payments.newPayment.additionalData.correspondentBank')}>
					<div className="space-y-space-16 my-space-16">
						<div>
							<ControlledTextInput
								disabled={formStatus !== FormStatus.INITIAL}
								isLoading={corrBic.isValidating}
								label={t('payments.newPayment.additionalData.swift.additionalData.bic')}
								name="correspondent.bic"
								placeholder="GUPULT22XXX"
								rules={{
									validate: corrBic.validate,
								}}
							/>
							{corrBic.warning && (
								<Typography
									className="mt-space-4 text-status-warning-500"
									level="small-text"
								>
									{corrBic.warning}
								</Typography>
							)}
						</div>

						<ControlledTextInput
							disabled={formStatus !== FormStatus.INITIAL || corrBic.isLocked}
							label={t('payments.newPayment.additionalData.swift.additionalData.name')}
							name="correspondent.name"
						/>
						<PaymentAddressInput<SwiftPayment>
							prefix="correspondent.address"
							translatePrefix="payments.newPayment.additionalData.address"
							disabled={formStatus !== FormStatus.INITIAL || corrBic.isLocked}
							unstructuredLineLabelKey="bankLine"
						/>
					</div>
				</Collapsible>

				<TemplateFromPayment disabled={formStatus !== FormStatus.INITIAL} />
				{failedToCreateTransaction ? (
					<Alert type={'error'}>{t('payments.common.orderFailed')}</Alert>
				) : (
					formStatus === FormStatus.CREATED && (
						<Alert type={'success'}>{t('payments.common.orderCreated')}</Alert>
					)
				)}
				<div>
					{formStatus === FormStatus.INITIAL && (
						<Button
							className="mt-6"
							disabled={isPending || !formState?.isValid}
							type="submit"
							variant="primary"
						>
							{t('payments.common.createButton')}
						</Button>
					)}
				</div>
			</form>
			{formStatus === FormStatus.CREATED && (
				<PermissionGate capability={Capability.SIGN_PAYMENT}>
					<div style={{ marginTop: '14px' }}>
						<PaymentTfa onSign={resetForm} transactionId={createdTransaction?.id} />
					</div>
				</PermissionGate>
			)}
			<FormBlocker
				hasChanges={
					Object.keys(formState.dirtyFields).length > 0 &&
					formStatus === FormStatus.INITIAL
				}
			/>
		</>
	);
};
